import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * Trusted by Industry Leaders — real BCS customer logos.
 * Logos served from /public/customer-logos/.
 *
 * Props:
 *   - featuredOnly: bool — when true, shows the curated marquee set (homepage).
 *                          When false, shows the full client roster (Customers page).
 *   - compact: bool — tighter top/bottom padding.
 *
 * `boost` per-client = visual scale multiplier (1 = default) to keep logos that
 *  have built-in padding (e.g. Trane) from looking smaller than the others.
 */

const ALL_CLIENTS = [
  // Featured / major (homepage)
  { name: 'Chevron',             file: 'chevron.png',           featured: true, boost: 1.05 },
  { name: 'Phillips 66',         file: 'p66.png',               featured: true, boost: 1.0 },
  { name: 'Marathon Petroleum',  file: 'marathon.png',          featured: true, boost: 1.05 },
  { name: 'Ford',                file: 'ford.png',              featured: true, boost: 1.0 },
  { name: 'Chrysler',            file: 'chrysler.png',          featured: true, boost: 1.0 },
  { name: 'Disney',              file: 'disney.png',            featured: true, boost: 1.0 },
  { name: 'U.S. Navy',           file: 'us-navy.png',           featured: true, boost: 1.35 },
  { name: 'Veolia',              file: 'veolia.png',            featured: true, boost: 1.0 },
  { name: 'Trane',               file: 'trane.png',             featured: true, boost: 1.5 },
  { name: 'Flowserve',           file: 'flowserve.png',         featured: true, boost: 1.15 },
  { name: 'CBRE',                file: 'cbre.png',              featured: true, boost: 1.05 },
  { name: 'GenOn Energy',        file: 'genon.png',             featured: true, boost: 1.0 },
  // Remaining roster (shown only on /customers page)
  { name: 'Knott’s Berry Farm',                file: 'knotts.png',                   boost: 1.4 },
  { name: 'Bender CCP',                        file: 'bender.png',                   boost: 1.0 },
  { name: 'LA Sanitation Districts',           file: 'lacsd.png',                    boost: 1.0 },
  { name: 'Orange County Sanitation',          file: 'ocsan.png',                    boost: 1.0 },
  { name: 'Inland Empire Utilities Agency',    file: 'ieua.png',                     boost: 1.0 },
  { name: 'Moulton Niguel Water District',     file: 'moulton-niguel.png',           boost: 1.0 },
  { name: 'San Diego County Water Authority',  file: 'san-diego-water.png',          boost: 1.0 },
  { name: 'City of Los Angeles',               file: 'city-of-la.png',               boost: 1.4 },
  { name: 'County of Los Angeles',             file: 'county-of-la.png',             boost: 1.35 },
  { name: 'LA County Public Works',            file: 'la-county-public-works.png',   boost: 1.0 },
  { name: 'LA County Public Health',           file: 'la-county-public-health.png',  boost: 1.0 },
  { name: 'UC Santa Barbara',                  file: 'ucsb.png',                     boost: 1.0 },
];

const TrustedBy = ({ compact = false, featuredOnly = false, hideHeader = false }) => {
  const clients = featuredOnly ? ALL_CLIENTS.filter(c => c.featured) : ALL_CLIENTS;

  return (
    <section
      className={`${compact ? 'py-14' : 'py-20'} px-4 sm:px-6 lg:px-8 bg-slate-50`}
      data-testid="trusted-by-section"
    >
      <div className="max-w-7xl mx-auto">
        {!hideHeader && (
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100">
              Trusted Partnerships
            </Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-3">
              Trusted by Industry Leaders
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From refineries and power plants to water districts and manufacturing facilities,
              organizations across the country rely on BCS to keep critical infrastructure running.
            </p>
          </div>
        )}

        {/* Logo grid — white cards, full-color logos, per-logo size boost */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {clients.map((client, idx) => {
            const boost = client.boost || 1;
            return (
              <div
                key={client.file}
                data-testid={`client-logo-${idx}`}
                title={client.name}
                className="group flex items-center justify-center bg-white border border-slate-200 rounded-xl px-3 py-4 hover:border-blue-900 hover:shadow-lg transition-all duration-200 h-32"
              >
                <img
                  src={`/customer-logos/${client.file}`}
                  alt={client.name}
                  loading="lazy"
                  style={{
                    maxHeight: `${72 * boost}px`,
                    maxWidth: `${88 * Math.min(boost, 1.2)}%`,
                  }}
                  className="object-contain"
                />
              </div>
            );
          })}
        </div>

        {featuredOnly && (
          <div className="text-center mt-10">
            <a
              href="/customers"
              data-testid="trusted-see-all-link"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-900 hover:bg-blue-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Read More About Our Customers
              <ArrowRight size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default TrustedBy;
